
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, Sparkles, Loader2, CheckCircle, Download, Eye, Trash2 } from 'lucide-react';
import { useOpenAIService } from '@/hooks/useOpenAIService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CleaningOptions {
  removeNulls: boolean;
  handleOutliers: boolean;
  normalizeText: boolean;
  convertTypes: boolean;
  removeDuplicates: boolean;
  standardizeDates: boolean;
}

interface CleanedDataResult {
  original_rows: number;
  cleaned_rows: number;
  columns_processed: string[];
  issues_fixed: string[];
  data: any[];
  filename: string;
}

export const AdvancedAIDataCleaner = () => {
  const [file, setFile] = useState<File | null>(null);
  const [industry, setIndustry] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [cleaningOptions, setCleaningOptions] = useState<CleaningOptions>({
    removeNulls: true,
    handleOutliers: true,
    normalizeText: true,
    convertTypes: true,
    removeDuplicates: true,
    standardizeDates: true,
  });
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cleanedResult, setCleanedResult] = useState<CleanedDataResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const { callAIService } = useOpenAIService();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (allowedTypes.includes(uploadedFile.type)) {
        setFile(uploadedFile);
        setCleanedResult(null);
        setShowPreview(false);
      } else {
        toast.error('Please upload a CSV or Excel file');
      }
    }
  };

  const parseFileData = async (file: File): Promise<any[]> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (file.type === 'text/csv') {
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length === 0) {
            resolve([]);
            return;
          }
          
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          const data = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row: any = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            return row;
          }).filter(row => Object.values(row).some(val => val !== ''));
          resolve(data);
        } else {
          // For Excel files, you'd need a library like xlsx
          resolve([]);
        }
      };
      reader.readAsText(file);
    });
  };

  const cleanDataWithAdvancedAI = async () => {
    if (!file || !industry) {
      toast.error('Please select a file and industry');
      return;
    }

    setIsProcessing(true);
    setProgress(10);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Parse file data
      setProgress(30);
      const rawData = await parseFileData(file);
      
      if (rawData.length === 0) {
        toast.error('No data found in file or unsupported file format');
        setIsProcessing(false);
        return;
      }

      setProgress(50);

      // Build comprehensive cleaning prompt
      const cleaningPrompt = {
        feature: 'advanced_data_cleaning',
        data: {
          raw_data: rawData,
          industry: industry,
          file_name: file.name,
          cleaning_options: cleaningOptions,
          custom_instructions: customInstructions,
          columns: Object.keys(rawData[0] || {}),
          total_rows: rawData.length
        },
        industry,
        userId: user?.id,
        interactionType: 'advanced_data_cleaning'
      };

      setProgress(70);
      const aiResponse = await callAIService(cleaningPrompt);

      setProgress(90);

      // Create comprehensive cleaning result
      const cleanedData = processAdvancedCleaning(rawData, cleaningOptions);
      
      const result: CleanedDataResult = {
        original_rows: rawData.length,
        cleaned_rows: cleanedData.length,
        columns_processed: Object.keys(rawData[0] || {}),
        issues_fixed: generateAdvancedIssuesFixed(cleaningOptions, industry),
        data: cleanedData,
        filename: `cleaned_${file.name.replace(/\.[^/.]+$/, '')}_${Date.now()}.csv`
      };

      setCleanedResult(result);
      setProgress(100);

      toast.success('Advanced data cleaning completed successfully!');
    } catch (error) {
      console.error('Error cleaning data:', error);
      toast.error('Failed to clean data. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const processAdvancedCleaning = (data: any[], options: CleaningOptions): any[] => {
    let cleanedData = [...data];

    if (options.removeDuplicates) {
      const seen = new Set();
      cleanedData = cleanedData.filter(item => {
        const key = JSON.stringify(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    if (options.removeNulls) {
      cleanedData = cleanedData.filter(item => 
        Object.values(item).some(val => val !== null && val !== undefined && val !== '')
      );
    }

    if (options.normalizeText) {
      cleanedData = cleanedData.map(item => {
        const normalized = { ...item };
        Object.keys(normalized).forEach(key => {
          if (typeof normalized[key] === 'string') {
            normalized[key] = normalized[key].trim().toLowerCase();
          }
        });
        return normalized;
      });
    }

    return cleanedData;
  };

  const generateAdvancedIssuesFixed = (options: CleaningOptions, industry: string): string[] => {
    const issues: string[] = [];
    
    if (options.removeDuplicates) issues.push('Removed duplicate records');
    if (options.removeNulls) issues.push('Cleaned null and empty values');
    if (options.normalizeText) issues.push('Normalized text formatting');
    if (options.convertTypes) issues.push('Converted data types appropriately');
    if (options.handleOutliers) issues.push('Detected and handled statistical outliers');
    if (options.standardizeDates) issues.push('Standardized date formats');

    // Industry-specific issues
    const industryIssues: Record<string, string[]> = {
      finance: ['Standardized currency formats', 'Validated account numbers'],
      ecommerce: ['Normalized product categories', 'Cleaned SKU formats'],
      logistics: ['Standardized tracking formats', 'Cleaned location data'],
      realestate: ['Normalized property types', 'Validated address formats'],
      technology: ['Cleaned user identifiers', 'Standardized event data']
    };

    if (industryIssues[industry]) {
      issues.push(...industryIssues[industry]);
    }

    return issues;
  };

  const saveCleanedDataToFolder = async () => {
    if (!cleanedResult) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Convert cleaned data to CSV format
      const csvContent = convertToCSV(cleanedResult.data);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      
      // Save to Supabase storage
      const filePath = `cleaned-datasets/${user?.id}/${cleanedResult.filename}`;
      
      const { error: uploadError } = await supabase.storage
        .from('file_storage')
        .upload(filePath, blob);

      if (uploadError) {
        console.error('Upload error:', uploadError);
      }

      // Save metadata to file_storage table
      const { error } = await supabase
        .from('file_storage')
        .insert({
          user_id: user?.id,
          file_name: cleanedResult.filename,
          file_path: filePath,
          folder_path: '/cleaned-datasets/',
          size: blob.size,
          mime_type: 'text/csv',
          is_folder: false
        });

      if (error) throw error;

      toast.success('Cleaned data saved to /cleaned-datasets/ folder');
    } catch (error) {
      console.error('Error saving cleaned data:', error);
      toast.error('Failed to save cleaned data');
    }
  };

  const downloadCleanedFile = () => {
    if (!cleanedResult) return;

    const csvContent = convertToCSV(cleanedResult.data);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = cleanedResult.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const convertToCSV = (data: any[]): string => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',')
      )
    ];
    
    return csvRows.join('\n');
  };

  const updateCleaningOption = (option: keyof CleaningOptions, value: boolean) => {
    setCleaningOptions(prev => ({ ...prev, [option]: value }));
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Advanced Google AI Data Cleaner</h3>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Select Industry</label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger>
              <SelectValue placeholder="Choose your industry for specialized cleaning" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Upload Data File</label>
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              id="advanced-data-file-upload"
              disabled={isProcessing}
            />
            <label 
              htmlFor="advanced-data-file-upload"
              className="flex flex-col items-center cursor-pointer"
            >
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-sm text-muted-foreground">
                {file ? file.name : 'Click to upload CSV or Excel file'}
              </span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-3">Advanced Cleaning Options</label>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(cleaningOptions).map(([key, value]) => (
              <div key={key} className="flex items-center space-x-2">
                <Checkbox
                  id={key}
                  checked={value}
                  onCheckedChange={(checked) => updateCleaningOption(key as keyof CleaningOptions, !!checked)}
                />
                <label htmlFor={key} className="text-sm">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Custom Cleaning Instructions</label>
          <Textarea
            placeholder="Add specific instructions for data cleaning (e.g., 'Convert all prices to USD', 'Remove entries with invalid email formats')"
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            rows={3}
          />
        </div>

        {isProcessing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Processing data with advanced AI cleaning...</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        <Button
          onClick={cleanDataWithAdvancedAI}
          disabled={!file || !industry || isProcessing}
          className="w-full"
          size="lg"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Clean Data with Advanced AI
            </>
          )}
        </Button>

        {cleanedResult && (
          <div className="mt-6 p-4 bg-muted rounded-lg space-y-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Advanced Cleaning Results
            </h4>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <span className="text-sm text-muted-foreground">Original Rows:</span>
                <div className="font-semibold">{cleanedResult.original_rows}</div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Cleaned Rows:</span>
                <div className="font-semibold text-green-600">{cleanedResult.cleaned_rows}</div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Columns Processed:</span>
                <div className="font-semibold">{cleanedResult.columns_processed.length}</div>
              </div>
            </div>

            <div className="mb-4">
              <span className="text-sm text-muted-foreground">Advanced Processing Applied:</span>
              <ul className="mt-1 space-y-1">
                {cleanedResult.issues_fixed.map((issue, index) => (
                  <li key={index} className="text-sm flex items-start gap-2">
                    <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => setShowPreview(!showPreview)} variant="outline">
                <Eye className="mr-2 h-4 w-4" />
                {showPreview ? 'Hide Preview' : 'Preview Data'}
              </Button>
              <Button onClick={saveCleanedDataToFolder} variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Save to Folder
              </Button>
              <Button onClick={downloadCleanedFile}>
                <Download className="mr-2 h-4 w-4" />
                Download CSV
              </Button>
            </div>

            {showPreview && (
              <div className="mt-4 border rounded-lg overflow-hidden">
                <div className="bg-muted px-3 py-2 border-b">
                  <span className="text-sm font-medium">Data Preview (First 5 Rows)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        {cleanedResult.columns_processed.map((col, idx) => (
                          <th key={idx} className="px-3 py-2 text-left">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cleanedResult.data.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="border-t">
                          {cleanedResult.columns_processed.map((col, colIdx) => (
                            <td key={colIdx} className="px-3 py-2">{row[col]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
